import type { AppSubjects } from "@lbcrm-api/casl/app.subjects";
import {
    type Icon,
    IconAddressBook,
    IconFileMusic,
    IconGavel,
    IconHexagons,
    IconHome2,
    IconIdBadge2,
    IconMusic,
    IconProgressCheck,
    IconSchool,
    IconSettings,
    IconShieldCheck,
    IconTag,
    IconUser,
    IconUsers,
} from "@tabler/icons-react";
import { useContext } from "react";
import { useMatch } from "react-router-dom";
import { AbilityContext } from "src/authentication/context/Can";
import { useAuth } from "src/authentication/hooks/useAuth";
import { Action } from "src/casl/Action";
import { ELDERS_DECISIONS_PATH } from "src/child-protection/clearances/elders/paths";
import { TRAINING_PATH } from "src/child-protection/clearances/training/paths";
import { ministryIconFor } from "src/ministries/components/ministryIcons";
import { useMinistryInvolvements } from "src/ministries/dashboard/useMinistryInvolvements";
import { hasActiveSeasons } from "src/ministries/ministrySeasonUtils";
import { useMainMenuItems } from "./useMainMenuItems";

export interface FrameLink {
    to: string;
    label: string;
    icon: Icon;
    end?: boolean;

    /** For a link whose path contains another item's, while that other item is the current one */
    forceInactive?: boolean;

    /** An inactive ministry: faded rather than struck through, so it still reads as itself */
    faded?: boolean;
}

export interface FrameGroup {
    key: string;
    label: string;
    icon: Icon;
    links: FrameLink[];
    inactive?: FrameLink[];
}

/**
 * Everything the rail and the More sheet list, with the permissions the sidebar
 * applied. It reads nothing the sidebar did not, so the frame fetches nothing new.
 */
export const useFrameMenu = () => {
    const main = useMainMenuItems();
    const { person } = useAuth();
    const ability = useContext(AbilityContext);
    const { ministryInvolvements } = useMinistryInvolvements();

    /** Training and the elders' screens live under the clearances path, so only one link reads as current there */
    const onTraining = useMatch({ path: TRAINING_PATH, end: false }) !== null;
    const onEldersApproval = useMatch({ path: ELDERS_DECISIONS_PATH, end: false }) !== null;

    const groups: FrameGroup[] = [];

    if (person && person.user.roles.length > 0) {
        if (ability.can(Action.Read, "Ministry")) {
            const toLink = (ministry: NonNullable<typeof ministryInvolvements>[number], faded = false): FrameLink => ({
                to: `/ministries/${ministry.id}`,
                label: ministry.name,
                icon: ministryIconFor(ministry.icon),
                faded,
            });

            groups.push({
                key: "ministries",
                label: "Ministries",
                icon: IconHexagons,
                links: (ministryInvolvements ?? []).filter(hasActiveSeasons).map((ministry) => toLink(ministry)),
                inactive: (ministryInvolvements ?? [])
                    .filter((ministry) => !hasActiveSeasons(ministry))
                    .map((ministry) => toLink(ministry, true)),
            });
        }

        if (ability.can(Action.Manage, "Person")) {
            const peopleLinks: (FrameLink & { subject: AppSubjects })[] = [
                { to: "/people/directory", label: "Directory", icon: IconAddressBook, subject: "Person" },
                { to: "/people/households", label: "Households", icon: IconHome2, subject: "Household" },
                { to: "/people/tags", label: "Tags", icon: IconTag, subject: "Tag" },
            ];

            groups.push({
                key: "people",
                label: "People",
                icon: IconUsers,
                links: peopleLinks.filter((link) => ability.can(Action.Read, link.subject)),
            });
        }

        /** Clearances have their own gate: a season manager holds consent but must not see them (`CLR59`) */
        const childProtection: FrameLink[] = [
            ...(ability.can(Action.Manage, "MinistryAttendanceConsent")
                ? [{ to: "/child-protection/forms/parental-consent-forms", label: "Parental consent and medical", icon: IconProgressCheck, end: true }]
                : []),
            ...(ability.can(Action.Manage, "ChildProtectionClearance")
                ? [{ to: "/child-protection/clearances", label: "Clearances", icon: IconIdBadge2, forceInactive: onTraining || onEldersApproval }]
                : []),
            ...(ability.can(Action.Manage, "ChildProtectionTraining")
                ? [{ to: TRAINING_PATH, label: "Training", icon: IconSchool }]
                : []),
            ...(ability.can(Action.Create, "ChildProtectionEldersDecision")
                ? [{ to: ELDERS_DECISIONS_PATH, label: "Elders' approval", icon: IconGavel }]
                : []),
        ];

        if (childProtection.length > 0) {
            groups.push({ key: "child-protection", label: "Child protection", icon: IconShieldCheck, links: childProtection });
        }

        if (ability.can(Action.Manage, "SongLibrarySong")) {
            groups.push({
                key: "songs",
                label: "Songs",
                icon: IconMusic,
                links: [{ to: "/song-libraries", label: "Song libraries", icon: IconFileMusic }],
            });
        }
    }

    const account: FrameLink[] = person
        ? [
              { to: "/public/profile", label: "My details", icon: IconUser },
              /** One way in, rather than a link per area: the index screen lists them */
              ...(ability.can(Action.Manage, "all") ? [{ to: "/settings", label: "Settings", icon: IconSettings }] : []),
          ]
        : [];

    return { main, groups, account };
};
